/**
 * Bedarfsgemeinschaft (BG) — Komposition nach SGB II.
 *
 * Datenmodell für die BG: jede Person mit Rolle, Alter, Sonderbedarfen.
 * Die Regelbedarfsstufe ergibt sich später regelbasiert aus Rolle+Alter
 * (siehe `packages/core/src/bg/`).
 *
 * KdU (Kosten der Unterkunft) bewusst aufgeteilt in Kalt/Neben/Heiz, weil
 * §§ 22 SGB II / § 35 SGB XII die Heizkosten gesondert behandeln und das
 * Jobcenter sie separat ausweist.
 */
import { z } from 'zod';
import { MoneyCentsSchema } from './brands.js';

export const BgRoleSchema = z.enum(['antragsteller', 'partner', 'kind']);
export type BgRole = z.infer<typeof BgRoleSchema>;

export const SpecialNeedSchema = z.enum([
  'pregnancy', // Schwangerschaft, § 21 Abs. 2 SGB II
  'single_parent', // Alleinerziehend, § 21 Abs. 3 SGB II
  'costly_nutrition', // kostenaufwändige Ernährung, § 21 Abs. 5 SGB II
  'disability_workintegration', // Eingliederungshilfe, § 21 Abs. 4 SGB II
  'decentralized_hot_water', // dezentrale Warmwasseraufbereitung, § 21 Abs. 7 SGB II
]);
export type SpecialNeed = z.infer<typeof SpecialNeedSchema>;

export const BgMemberSchema = z.object({
  /** Rolle in der Bedarfsgemeinschaft */
  role: BgRoleSchema,
  /** Alter in Jahren am Stichtag */
  age: z.number().int().min(0).max(120),
  /** Mehrbedarfe nach § 21 SGB II (leeres Array wenn keiner) */
  specialNeeds: z.array(SpecialNeedSchema).default([]),
});
export type BgMember = z.infer<typeof BgMemberSchema>;

/**
 * KdU-Felder sind per Definition nicht-negativ (Miete/Nebenkosten/Heizkosten
 * können nicht negativ sein). Wir validieren das hier, weil `MoneyCents`
 * generisch auch negative Beträge erlaubt (z.B. negative Liquidität).
 */
const NonNegativeMoneyCentsSchema = MoneyCentsSchema.refine((v) => v >= 0, {
  message: 'KdU-Beträge müssen ≥ 0 sein',
});

export const KduSchema = z.object({
  kaltmiete: NonNegativeMoneyCentsSchema,
  nebenkosten: NonNegativeMoneyCentsSchema,
  heizkosten: NonNegativeMoneyCentsSchema,
});
export type Kdu = z.infer<typeof KduSchema>;

export const BgCompositionSchema = z
  .object({
    members: z.array(BgMemberSchema).min(1, 'BG braucht mindestens den Antragsteller'),
    kdu: KduSchema,
  })
  .refine((bg) => bg.members.filter((m) => m.role === 'antragsteller').length === 1, {
    message: 'Genau ein Antragsteller pro BG',
    path: ['members'],
  })
  .refine((bg) => bg.members.filter((m) => m.role === 'partner').length <= 1, {
    message: 'Maximal ein Partner pro BG',
    path: ['members'],
  });
export type BgComposition = z.infer<typeof BgCompositionSchema>;
